import { useMemo, useState } from 'react';
import type { LiveGame, Pilot, PowerLevel } from '@edh/shared';
import {
  PLACEMENT_GAMES,
  deckLabel,
  display,
  isDraft,
  isRanked,
  readyToStart,
  roman,
  seatedEntries,
} from '@edh/shared';
import { DeckThumb } from './DeckThumb';
import { GuildMark } from './GuildMark';
import { RankBadge } from './RankBadge';
import { RankedAvatar } from './RankedAvatar';
import { DeckPicker, PlayerPicker } from './SeatPickers';
import { CommentaryPanel } from './Commentary';
import type { CommentaryFeed } from '../lib/commentary';
import { TableOdds } from './TableOdds';
import { claimSeat, endGame, startGame } from '../lib/live';
import { useStore } from '../lib/store';

/**
 * A table with chairs round it, before anybody has played a card.
 *
 * The difference from the old setup screen is who is filling it in. This one is
 * open: it exists on the server the moment the host makes it, everyone in the
 * guild sees it on their Play tab, and each person takes their own chair and
 * picks their own deck from their own phone.
 *
 * The host can still do all of it for everyone, and that is not a leftover —
 * not everyone at a Commander table is holding a phone. Guests, a dead battery,
 * the friend who has not installed it. A join feature that quietly made those
 * people unplayable would be a worse app than the one it replaced.
 *
 * Empty chairs are dropped when the game starts, so the host never has to
 * declare how many are coming. Open a table, whoever sits down plays.
 */

/** The number under a face. Nothing during placement pretends to be a rating. */
function Rating({
  elo,
  played,
  bracket,
  pilot,
}: {
  elo: number;
  played: number;
  bracket?: PowerLevel | null;
  pilot?: Pilot;
}) {
  const ranked = isRanked(played);
  return (
    <span className="seat-half-rating num">
      {ranked ? display(elo) : `${played}/${PLACEMENT_GAMES}`}
      {bracket && <span className="seat-half-bracket">{roman(bracket)}</span>}
      {pilot && ranked && <RankBadge pilot={pilot} size="small" />}
    </span>
  );
}

/*
 * The arrival animation is gone, on purpose.
 *
 * A seat being filled used to light the card: a band of gold swept across it,
 * the face landed, the text followed. It read well and it is the wrong thing
 * to have here. Phones were crashing in the lobby, and this was the only thing
 * on the screen doing work every frame — a gradient travelling across a card
 * repaints the whole card each time, up to eight cards can fire at once while
 * a table fills, and all of it lands on whatever phone somebody happens to
 * own. A lobby you cannot get into is worse than a lobby that fills quietly.
 *
 * Whether it was truly the cause is not established; it is the suspect with
 * both the means and the timing, and it is the cheapest one to remove. Removed
 * first, so the next crash — if there is one — has something new to say.
 *
 * The hook that drove it (`useArrivals`, watching for seats that *changed*,
 * since a shared lobby is read through a poll) went with it. Git has it if
 * this comes back, and it should come back on a transform rather than a
 * background position.
 */

export function Lobby({
  game,
  onChange,
  onLeave,
  onStarted,
  feed,
}: {
  game: LiveGame;
  /** The peanut gallery, already arguing about the odds above it. */
  feed: CommentaryFeed;
  /** A call that already returned the new document; no need to wait for a poll. */
  onChange: (game: LiveGame) => void;
  onLeave: () => void;
  onStarted: (game: LiveGame) => void;
}) {
  const { state, me } = useStore();
  const [picking, setPicking] = useState<{ seat: number; what: 'player' | 'deck' } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const members = useMemo(() => state.pilots.filter((p) => !p.retired), [state.pilots]);
  const decks = useMemo(
    () => [...state.decks].filter((d) => !d.retired).sort((a, b) => a.name.localeCompare(b.name)),
    [state.decks],
  );

  const draft = isDraft(game);
  const host = me?.id === game.hostPilotId;
  const mySeat = game.seats.findIndex((s) => s.pilotId === me?.id);
  const seated = seatedEntries(game);
  const ready = readyToStart(game);

  async function act(fn: () => Promise<LiveGame>) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      onChange(await fn());
    } catch (err) {
      // Usually somebody took the chair a second before you did, which the next
      // poll would explain anyway — but not saying so leaves a tap that did
      // nothing and no reason why.
      setError(err instanceof Error ? err.message : 'that did not work');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Leaving, which for the host means the table goes with them.
   *
   * A lobby the host has walked away from is a lobby nobody can start — and it
   * keeps its place, so everyone else opening Play lands back in it. Ending it
   * is what walking away actually meant.
   *
   * Failures are swallowed on purpose. The navigation is the part the tap
   * asked for; a table that outlives it is swept away when somebody opens the
   * next one anyway.
   */
  async function leave() {
    if (busy) return;
    if (host) {
      setBusy(true);
      await endGame(game.id).catch(() => {});
      setBusy(false);
    }
    onLeave();
  }

  /** Who currently holds each one, so the picker can say so instead of refusing. */
  const heldBy = (key: 'pilotId' | 'deckId'): Record<string, string> => {
    const held: Record<string, string> = {};
    game.seats.forEach((s, i) => {
      const id = s[key];
      if (!id) return;
      held[id] = members.find((p) => p.id === s.pilotId)?.name ?? `seat ${i + 1}`;
    });
    return held;
  };

  return (
    <>
      <div className="contest-head">
        {/*
          Back closes the table for the host, rather than walking away from one
          still standing.

          It used to only navigate, which left the lobby open on the server —
          so coming back to Play dropped you straight into it again and the only
          real way out was a separate button underneath. Two controls for one
          intention, and the obvious one was the one that did not work.

          Only the host ends it. For anybody else this is still just leaving:
          their seat is theirs to give up and the table is not theirs to close.
        */}
        <button className="back-chevron" onClick={leave} aria-label="Back" disabled={busy}>
          ‹
        </button>
        <GuildMark name={state.settings.guildName} src={state.settings.guildIcon} size={72} />
        <div>
          {/* Named for what it is, not just whose it is. There will be more
              than one kind of contest before long and "Misadventures" alone
              would not say which one you had opened. */}
          {/* The mode, at last read off the game rather than asserted. */}
          <h2 className="contest-title">
            {state.settings.guildName} {draft ? 'Draft' : 'Standard'} Contest
          </h2>
          <p className="contest-sub">
            {seated.length < 2
              ? 'Waiting for players…'
              : `${seated.length} seated${host ? '' : ' · waiting for the host'}`}
          </p>
        </div>
      </div>

      {/*
        Only the half that tells somebody something they cannot see.

        The host's copy explained that the table is shared and that people can
        take their own chairs — true when this was new, and by now the empty
        seats and the chevron say it better than a paragraph does. A guest
        arriving still needs to know they are waiting on somebody else.
      */}
      {/*
        Only what is still left to do.

        Joining seats you now, so telling a guest to take a chair is telling
        them to do the thing they just did. And in a draft there is no deck to
        pick — that is the whole mode — so the line that used to say so was
        instructions for a screen they were not on.
      */}
      {!host && (draft || !game.seats[mySeat]?.deckId) && (
        <p className="lobby-note">
          {draft
            ? 'Your deck comes from the draft. The host starts it.'
            : mySeat === -1
              ? 'Take a chair and pick your deck. The host starts the game.'
              : 'Pick your deck. The host starts the game.'}
        </p>
      )}

      {game.seats.map((seat, i) => {
        const pilot = members.find((p) => p.id === seat.pilotId);
        const deck = decks.find((d) => d.id === seat.deckId);
        const isMine = seat.pilotId === me?.id;
        // Your own chair, an empty one, or anyone's if you are running the table.
        const canEditPlayer = host || (!seat.pilotId && mySeat === -1) || isMine;
        // In a draft nobody chooses a deck here; that is what the draft is for.
        const canEditDeck = !draft && (host || isMine);

        return (
          <div key={i} className={`seat-card${isMine ? ' is-mine' : ''}`}>
            <div className="seat-split">
              <span className="seat-x" aria-hidden="true">
                ×
              </span>

              {/*
                Getting up from the table.

                There was a way to do this already — open the picker and tap the
                person who is already in the chair — and nobody would ever find
                it. Somebody joining the wrong table, or a friend who has to
                leave before you start, is common enough to be one tap.

                Yours or, if you are running the table, anybody's.
              */}
              {seat.pilotId && canEditPlayer && (
                <button
                  className="seat-remove"
                  disabled={busy}
                  aria-label={`take ${isMine ? 'yourself' : (pilot?.name ?? 'this player')} out of the game`}
                  onClick={() =>
                    void act(() =>
                      claimSeat(game.id, { seat: i, pilotId: seat.pilotId, leave: true }),
                    )
                  }
                >
                  ×
                </button>
              )}

              <button
                className="seat-half"
                disabled={!canEditPlayer || busy}
                onClick={() => {
                  // Tapping an empty chair when you have none is the whole
                  // gesture: sit down. No picker, no list, one tap.
                  if (!seat.pilotId && !host && mySeat === -1) {
                    void act(() => claimSeat(game.id, { seat: i }));
                    return;
                  }
                  setPicking({ seat: i, what: 'player' });
                }}
              >
                {pilot ? (
                  <>
                    <RankedAvatar pilot={pilot} size={58} />
                    <span className="seat-half-text">
                      <span className="seat-half-name">
                        {pilot.name}
                        {seat.pilotId === game.hostPilotId && (
                          // A real space, not a margin: the name and the tag are
                          // inline, so they were butting up against each other.
                          <>
                            {' '}
                            <span className="pip gold">host</span>
                          </>
                        )}
                      </span>
                      <Rating elo={pilot.elo} played={pilot.placementGames} pilot={pilot} />
                    </span>
                  </>
                ) : (
                  <>
                    <span className="seat-half-empty">
                      {/* Drawn rather than typed. A "+" glyph carries its own
                          side bearings and sits on the text baseline, so it
                          never lands in the middle of a circle however the box
                          is centred — which is exactly what it looked like. */}
                      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                        <path
                          d="M12 6v12M6 12h12"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                    <span className="seat-half-name muted">
                      {mySeat === -1 && !host ? 'Take a seat' : 'Add player'}
                    </span>
                  </>
                )}
              </button>

              <button
                className="seat-half"
                disabled={!canEditDeck || !seat.pilotId || busy}
                onClick={() => setPicking({ seat: i, what: 'deck' })}
              >
                {deck ? (
                  <>
                    <DeckThumb deck={deck} size={58} />
                    <span className="seat-half-text">
                      <span className="seat-half-name">{deckLabel(deck)}</span>
                      <Rating
                        elo={deck.elo}
                        played={deck.placementGames}
                        bracket={deck.powerLevel}
                      />
                    </span>
                  </>
                ) : (
                  <>
                    <span className="seat-half-empty">
                      {/* Drawn rather than typed. A "+" glyph carries its own
                          side bearings and sits on the text baseline, so it
                          never lands in the middle of a circle however the box
                          is centred — which is exactly what it looked like. */}
                      {!draft && (
                        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                          <path
                            d="M12 6v12M6 12h12"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                        </svg>
                      )}
                    </span>
                    <span className="seat-half-name muted">
                      {draft ? 'Drafted' : seat.pilotId ? 'Pick a deck' : ''}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        );
      })}

      {picking?.what === 'player' && (
        <PlayerPicker
          members={members}
          heldBy={heldBy('pilotId')}
          selected={game.seats[picking.seat]?.pilotId ?? ''}
          onPick={(pilotId) => {
            const held = game.seats[picking.seat]?.pilotId;
            void act(() =>
              held === pilotId
                ? claimSeat(game.id, { seat: picking.seat, pilotId, leave: true })
                : claimSeat(game.id, { seat: picking.seat, pilotId }),
            );
            setPicking(null);
          }}
          onClose={() => setPicking(null)}
        />
      )}

      {picking?.what === 'deck' && (
        <DeckPicker
          decks={decks}
          heldBy={heldBy('deckId')}
          selected={game.seats[picking.seat]?.deckId ?? ''}
          onPick={(deckId) => {
            const held = game.seats[picking.seat]?.deckId;
            void act(() =>
              claimSeat(game.id, {
                seat: picking.seat,
                pilotId: game.seats[picking.seat]?.pilotId,
                // Tapping what a seat already holds clears it.
                deckId: held === deckId ? null : deckId,
              }),
            );
            setPicking(null);
          }}
          onClose={() => setPicking(null)}
        />
      )}

      {error && <p className="error">{error}</p>}

      {/* Only once the table is set — partial odds would be noise, and the feed
          has nothing to have an opinion about until the seats are filled. */}
      {ready && (
        <>
          <TableOdds
            seats={seated.map((e) => ({
              pilot: members.find((p) => p.id === e.pilotId)!,
              deck: decks.find((d) => d.id === e.deckId)!,
            }))}
            analysis={game.analysis?.text}
          />
          <CommentaryPanel feed={feed} />
        </>
      )}

      {host ? (
        <>
          <button
            className="btn primary block"
            style={{ marginTop: 14, padding: '15px 16px', fontSize: 17 }}
            disabled={!ready || busy}
            onClick={() => void act(async () => {
              const started = await startGame(game.id);
              onStarted(started);
              return started;
            })}
          >
            {busy ? <span className="spinner" /> : 'Begin'}
          </button>
        </>
      ) : (
        <div className="lobby-waiting">
          <span className="spinner" />
          <span>Waiting for the host to start</span>
        </div>
      )}
    </>
  );
}
